/**
 * Pantalla de Parámetros del sistema — Fase 8
 *
 * Lista los parámetros editables (tabla `parametros`) con su valor
 * actual + descripción + tipo. Solo `admin_general` puede acceder
 * (matriz M14). Click en una fila abre el dialog de edición.
 *
 * No permite crear ni eliminar — los params son del sistema.
 */

import { useState, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';

import { useParametros } from '../../hooks/useParametros';
import { formatDateTime } from '../../utils/format';
import {
  type Parametro,
  getTipoLabel,
  getTipoColor,
} from '../../types/parametro';
import ParametroDialog from './ParametroDialog';

export default function ParametrosList() {
  const { data: parametros = [], isLoading, isError, error } = useParametros();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Parametro | null>(null);

  const openEdit = (p: Parametro) => {
    setEditing(p);
    setDialogOpen(true);
  };

  const columns = useMemo<GridColDef<Parametro>[]>(
    () => [
      {
        field: 'clave',
        headerName: 'Clave',
        flex: 1,
        minWidth: 220,
        renderCell: (p: GridRenderCellParams<Parametro>) => (
          <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
            {p.row.clave}
          </Typography>
        ),
      },
      {
        field: 'descripcion',
        headerName: 'Descripción',
        flex: 2,
        minWidth: 250,
        renderCell: (p) => (
          <Typography variant="body2" color="text.secondary">
            {p.row.descripcion ?? '—'}
          </Typography>
        ),
      },
      {
        field: 'tipo',
        headerName: 'Tipo',
        width: 110,
        renderCell: (p: GridRenderCellParams<Parametro>) => (
          <Chip
            label={getTipoLabel(p.row.tipo)}
            size="small"
            color={getTipoColor(p.row.tipo)}
            variant="outlined"
          />
        ),
      },
      {
        field: 'valor',
        headerName: 'Valor actual',
        width: 180,
        renderCell: (p: GridRenderCellParams<Parametro>) => (
          <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
            {p.row.valor}
          </Typography>
        ),
      },
      {
        field: 'updated_at',
        headerName: 'Última modificación',
        width: 160,
        renderCell: (p: GridRenderCellParams<Parametro>) => (
          <Typography variant="caption" color="text.secondary">
            {formatDateTime(p.row.updated_at)}
          </Typography>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 70,
        sortable: false,
        filterable: false,
        renderCell: (p: GridRenderCellParams<Parametro>) => (
          <Tooltip title="Editar parámetro">
            <IconButton size="small" onClick={() => openEdit(p.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [],
  );

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <SettingsIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Parámetros del sistema
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Valores configurables que afectan el comportamiento global. Los
            cambios se aplican en tiempo real.
          </Typography>
        </Box>
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar parámetros: {(error as Error)?.message ?? 'desconocido'}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Los parámetros se editan acá sin necesidad de tocar la base de datos.
        Para <strong>agregar parámetros nuevos</strong> se hace via migration
        (los crea el equipo de desarrollo).
      </Alert>

      <Card>
        <DataGrid
          rows={parametros}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.clave}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ border: 0 }}
          localeText={{
            noRowsLabel: 'No hay parámetros configurados',
          }}
        />
      </Card>

      <ParametroDialog
        open={dialogOpen}
        parametro={editing}
        onClose={() => setDialogOpen(false)}
      />
    </Box>
  );
}
